/**
 * Monta e relê a expressão JEXL dos alarmes personalizados.
 *
 * Isolado da tela porque é a única parte com regra de correção de verdade, e o erro aqui é mudo:
 * expressão inválida não avisa ninguém, só deixa de disparar.
 *
 * Forma canônica, a única que `parseExpression` reconhece:
 *   <termo> [&& | ||] <termo> ... ? 'NOME' : null
 * Com mais de um termo todos vão entre parênteses, para a precedência de `&&` sobre `||` nunca
 * mudar o sentido do que foi montado na tela.
 */

export type Operator = 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'in' | 'between' | 'digitOn';

/**
 * `digitOn` existe porque rastreador reporta entrada digital como máscara num campo só.
 * O Suntech manda `00000101` e o Traccar grava o número 101: a casa das unidades é a
 * ignição e a das centenas é a entrada 2 (medido no equipamento 2200003213 em 2026-08-10).
 *
 * Sem aritmética, isolar uma entrada obrigaria a enumerar todo valor possível da máscara
 * (`input == 100 || input == 101`), e a regra passaria a errar em silêncio no dia em que
 * outra entrada fosse acionada junto, porque o número vira outro. `input % 1000 >= 100`
 * pergunta só pela casa que interessa e ignora o resto da máscara.
 *
 * O `value` desta condição é a CASA, contada da direita para a esquerda a partir de 1.
 */
const digitDivisors = (casa: number): { divisor: number; piso: number } => {
  const piso = 10 ** (casa - 1);
  return { divisor: piso * 10, piso };
};

const parseCasa = (raw: string): number | null => {
  const n = Number(String(raw ?? '').trim());
  return Number.isInteger(n) && n >= 1 && n <= 9 ? n : null;
};

export interface Condition {
  attribute: string;
  operator: Operator;
  /** Para `in`, lista separada por vírgula. Para `between`, o limite inferior. */
  value: string;
  value2?: string;
  /**
   * Literal citado apesar de numérico (`in1 == "1"`). Preserva o sentido na EDIÇÃO: reescrever como
   * `== 1` trocaria comparação de texto por numérica e quebraria uma regra que funcionava.
   */
  quoted?: boolean;
}

export interface RuleDraft {
  name: string;
  logic: 'and' | 'or';
  conditions: Condition[];
}

export const OPERATOR_LABEL: Record<Operator, string> = {
  eq: 'é igual a',
  ne: 'é diferente de',
  gt: 'é maior que',
  lt: 'é menor que',
  gte: 'é maior ou igual a',
  lte: 'é menor ou igual a',
  in: 'está na lista',
  between: 'está entre',
  digitOn: 'tem ligada a entrada da casa',
};

export const OPERATOR_OPTIONS = (Object.keys(OPERATOR_LABEL) as Operator[]).map((value) => ({
  value,
  label: OPERATOR_LABEL[value],
}));

const SIMPLE_OP: Partial<Record<Operator, string>> = {
  eq: '==',
  ne: '!=',
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
};

/** Números e booleanos crus; o resto entre aspas simples, que o JEXL aceita escapar. */
export const literal = (raw: string, forceQuoted = false): string => {
  const v = raw ?? '';
  const t = v.trim();
  if (!forceQuoted) {
    // `trim` só CLASSIFICA; o ramo texto devolve o original, senão apaga espaço que é do valor.
    if (/^-?\d+(\.\d+)?$/.test(t)) return t;
    if (t === 'true' || t === 'false') return t;
  }
  return `'${v.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
};

/** Desfaz `literal`. Lê aspas simples e duplas, porque expressão escrita à mão mistura as duas. */
const unliteral = (token: string): { value: string; quoted: boolean } => {
  const v = token.trim();
  const single = v.match(/^'((?:[^'\\]|\\.)*)'$/);
  if (single) return { value: single[1].replace(/\\'/g, "'").replace(/\\\\/g, '\\'), quoted: true };
  const double = v.match(/^"((?:[^"\\]|\\.)*)"$/);
  if (double) return { value: double[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\'), quoted: true };
  return { value: v, quoted: false };
};

/** Só importa quando o valor PARECE número: fora disso o literal já sai citado. */
const keepQuoted = (c: Condition): boolean =>
  !!c.quoted && /^-?\d+(\.\d+)?$/.test(String(c.value ?? '').trim());

export const renderTerm = (c: Condition): string => {
  const attr = c.attribute.trim();
  const q = keepQuoted(c);
  if (c.operator === 'digitOn') {
    const casa = parseCasa(c.value);
    if (casa === null) return '';
    const { divisor, piso } = digitDivisors(casa);
    return `${attr} % ${divisor} >= ${piso}`;
  }
  if (c.operator === 'in') {
    const parts = String(c.value ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return '';
    return `(${parts.map((p) => `${attr} == ${literal(p, c.quoted)}`).join(' || ')})`;
  }
  if (c.operator === 'between') {
    return `(${attr} >= ${literal(c.value, q)} && ${attr} <= ${literal(c.value2 ?? '', q)})`;
  }
  return `${attr} ${SIMPLE_OP[c.operator]} ${literal(c.value, q)}`;
};

export const buildExpression = (draft: RuleDraft): string => {
  const terms = draft.conditions.map(renderTerm).filter(Boolean);
  if (!terms.length) return '';
  const join = draft.logic === 'or' ? ' || ' : ' && ';
  const body =
    terms.length === 1 ? terms[0] : terms.map((t) => (t.startsWith('(') ? t : `(${t})`)).join(join);
  // Nome sempre citado: `? 123 : null` gravaria número onde o sistema espera o texto do alarme.
  return `${body} ? ${literal(draft.name, true)} : null`;
};

/** Descrição em português para a listagem. */
export const describeDraft = (draft: RuleDraft): string => {
  const join = draft.logic === 'or' ? ' OU ' : ' E ';
  return draft.conditions
    .map((c) => {
      if (c.operator === 'between') return `${c.attribute} entre ${c.value} e ${c.value2 ?? ''}`;
      if (c.operator === 'digitOn') return `${c.attribute} com a entrada da casa ${c.value} ligada`;
      return `${c.attribute} ${OPERATOR_LABEL[c.operator]} ${c.value}`;
    })
    .join(join);
};

/** Quebra por `&&`/`||` no nível zero de parênteses e fora de string. */
const splitTopLevel = (input: string): { parts: string[]; logic: 'and' | 'or' } | null => {
  const parts: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let escaped = false;
  let start = 0;
  let logic: 'and' | 'or' | null = null;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quote) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === quote) quote = null;
      continue;
    }
    if (ch === "'" || ch === '"') {
      quote = ch;
      continue;
    }
    if (ch === '(') {
      depth++;
      continue;
    }
    if (ch === ')') {
      depth--;
      continue;
    }
    if (depth !== 0) continue;
    const pair = input.slice(i, i + 2);
    if (pair === '&&' || pair === '||') {
      const found: 'and' | 'or' = pair === '&&' ? 'and' : 'or';
      // Conectivos misturados a tela não gera: devolve nulo e a edição cai no modo avançado.
      if (logic && logic !== found) return null;
      logic = found;
      parts.push(input.slice(start, i));
      start = i + 2;
      i++;
    }
  }
  parts.push(input.slice(start));
  return { parts: parts.map((p) => p.trim()).filter(Boolean), logic: logic ?? 'and' };
};

const stripOuterParens = (s: string): string => {
  const v = s.trim();
  if (!v.startsWith('(') || !v.endsWith(')')) return v;
  // Só remove se o parêntese inicial casa com o final, senão comeria `(a) && (b)`.
  let depth = 0;
  for (let i = 0; i < v.length; i++) {
    if (v[i] === '(') depth++;
    else if (v[i] === ')') {
      depth--;
      if (depth === 0) return i === v.length - 1 ? v.slice(1, -1).trim() : v;
    }
  }
  return v;
};

const parseTerm = (rawTerm: string): Condition | null => {
  const term = rawTerm.trim();
  const inner = stripOuterParens(term);

  if (inner !== term) {
    // `between`: (a >= x && a <= y)
    const bt = inner.match(/^(\S+)\s*>=\s*(.+?)\s*&&\s*(\S+)\s*<=\s*(.+)$/);
    if (bt && bt[1] === bt[3]) {
      const lo = unliteral(bt[2]);
      const hi = unliteral(bt[4]);
      return {
        attribute: bt[1],
        operator: 'between',
        value: lo.value,
        value2: hi.value,
        quoted: lo.quoted && hi.quoted,
      };
    }
    // `in`: (a == v1 || a == v2 || ...)
    const split = splitTopLevel(inner);
    if (split && split.logic === 'or' && split.parts.length >= 1) {
      const eqs = split.parts.map((p) => p.match(/^(\S+)\s*==\s*(.+)$/));
      if (eqs.every(Boolean)) {
        const attrs = new Set(eqs.map((m) => m![1]));
        if (attrs.size === 1) {
          const parsed = eqs.map((m) => unliteral(m![2]));
          return {
            attribute: eqs[0]![1],
            operator: 'in',
            value: parsed.map((p) => p.value).join(', '),
            quoted: parsed.every((p) => p.quoted),
          };
        }
      }
    }
  }

  // Antes da comparação simples: `input % 1000 >= 100` também termina em `>= <número>`,
  // e sem esta leitura a regra da entrada digital cairia no modo avançado toda vez.
  const dg = inner.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*%\s*(\d+)\s*>=\s*(\d+)$/);
  if (dg) {
    const divisor = Number(dg[2]);
    const piso = Number(dg[3]);
    if (piso > 0 && divisor === piso * 10) {
      const casa = String(piso).length; // 1 → casa 1, 100 → casa 3
      return { attribute: dg[1], operator: 'digitOn', value: String(casa) };
    }
  }

  // `>=`/`<=`/`!=`/`==` antes de `>`/`<`, senão a alternância corta cedo demais.
  const m = inner.match(/^(\S+)\s*(>=|<=|!=|==|>|<)\s*(.+)$/);
  if (!m) return null;
  const opByToken: Record<string, Operator> = {
    '==': 'eq',
    '!=': 'ne',
    '>': 'gt',
    '<': 'lt',
    '>=': 'gte',
    '<=': 'lte',
  };
  const parsed = unliteral(m[3]);
  return { attribute: m[1], operator: opByToken[m[2]], value: parsed.value, quoted: parsed.quoted };
};

/** Relê o que ESTA tela gerou; `null` para o resto, que abre em modo avançado com o texto cru. */
export const parseExpression = (expression: string): RuleDraft | null => {
  const expr = (expression || '').trim();
  const m = expr.match(
    /^([\s\S]+?)\s*\?\s*(?:'((?:[^'\\]|\\.)*)'|"((?:[^"\\]|\\.)*)")\s*:\s*null$/,
  );
  if (!m) return null;
  const rawName = m[2] !== undefined ? m[2] : m[3];
  const split = splitTopLevel(m[1]);
  if (!split || !split.parts.length) return null;
  const conditions: Condition[] = [];
  for (const part of split.parts) {
    const c = parseTerm(part);
    if (!c) return null;
    conditions.push(c);
  }
  return {
    name: rawName.replace(/\\'/g, "'").replace(/\\"/g, '"').replace(/\\\\/g, '\\'),
    logic: split.logic,
    conditions,
  };
};

/**
 * O nome é gravado em `alarm` E é a chave que `attributes.alarms` compara por igualdade exata: se os
 * dois lados não baterem, o evento nasce e ninguém é notificado. Vírgula é proibida porque o filtro
 * é lista separada por vírgula; aspa simples, para o nome nunca precisar de escape no JEXL.
 */
export const validateRuleName = (name: string): string | null => {
  const v = (name || '').trim();
  if (!v) return 'Informe o nome do alarme.';
  if (v.includes(',')) return 'O nome não pode conter vírgula (o filtro de alarmes é separado por vírgula).';
  if (v.includes("'")) return 'O nome não pode conter aspa simples.';
  if (v.length > 100) return 'O nome deve ter no máximo 100 caracteres.';
  return null;
};
